import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { ProcessedManifestCorrectionStatsQuery } from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useTenant } from "hooks/useTenant";

import { PROCESSED_MANIFEST_CORRECTION_STATS_QUERY } from "./api";
import { upsertOmnisearchTerm } from "./omnisearch";

interface Props {
  shippedYear: number;
  skip?: boolean;
}

export function CorrectionComplianceTile({ shippedYear, skip = false }: Props) {
  const { tenantId } = useTenant();
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();

  const { data, loading, error } = useQuery(PROCESSED_MANIFEST_CORRECTION_STATS_QUERY, {
    variables: { tenantId: tenantId ?? "", shippedYear },
    skip: skip || !tenantId,
  });

  // Clicking a composition-bar segment filters the manifests grid to that bucket.
  // Only the correctionBucket term is set; tenantId/shippedDate come from the
  // grid's defaultSearch (smart-merged).
  const filterByBucket = (bucket: string) => {
    setFilters((prev) => ({
      ...prev,
      omnisearch: upsertOmnisearchTerm(prev.omnisearch ?? "", "correctionBucket", bucket),
    }));
  };

  return (
    <DashboardTile size={{ xs: 12, md: 6, lg: 4 }}>
      <DashboardTile.Header title="30-Day Correction Compliance" />
      <DashboardTile.ContentArea>
        {renderBody({ loading, error, data, shippedYear, onBucketClick: filterByBucket })}
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
}

function renderBody({
  loading,
  error,
  data,
  shippedYear,
  onBucketClick,
}: {
  loading: boolean;
  error: unknown;
  data: ProcessedManifestCorrectionStatsQuery | undefined;
  shippedYear: number;
  onBucketClick: (bucket: string) => void;
}) {
  if (loading) {
    return (
      <Stack spacing={1} data-testid="tile-skeleton">
        <Skeleton variant="text" width={120} height={56} />
        <Skeleton variant="text" width={180} />
        <Skeleton variant="rectangular" height={12} />
      </Stack>
    );
  }

  if (error) {
    return <Typography color="error">Couldn't load 30-day compliance stats</Typography>;
  }

  const stats = data?.processedManifestCorrectionStats;
  if (!stats) {
    return null;
  }

  const concluded = stats.onTime + stats.late + stats.overdue;

  if (concluded === 0 && stats.inWindow === 0) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="h3">—</Typography>
        <Typography variant="body2" color="text.secondary">
          No corrections requested in {shippedYear}
        </Typography>
      </Stack>
    );
  }

  const headline = concluded > 0 ? `${Math.round((stats.onTime / concluded) * 100)}%` : "—";

  const sublineParts: string[] = [];
  if (concluded > 0) {
    sublineParts.push(`${stats.onTime} of ${concluded} resolved on time`);
  }
  if (stats.inWindow > 0) {
    sublineParts.push(`${stats.inWindow} pending`);
  }

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3">{headline}</Typography>
        <Typography variant="body2" color="text.secondary">
          {sublineParts.join(" · ")}
        </Typography>
      </Stack>
      <CompositionBar
        onTime={stats.onTime}
        late={stats.late}
        overdue={stats.overdue}
        inWindow={stats.inWindow}
        onBucketClick={onBucketClick}
      />
    </Stack>
  );
}

interface Segment {
  key: "onTime" | "late" | "overdue" | "inWindow";
  /** The correctionBucket value used by the grid omnisearch filter. */
  bucket: "ON_TIME" | "LATE" | "OVERDUE" | "PENDING";
  label: string;
  value: number;
  color: string;
}

function CompositionBar({
  onTime,
  late,
  overdue,
  inWindow,
  onBucketClick,
}: {
  onTime: number;
  late: number;
  overdue: number;
  inWindow: number;
  onBucketClick: (bucket: string) => void;
}) {
  const theme = useTheme();
  const allSegments: Segment[] = [
    {
      key: "onTime",
      bucket: "ON_TIME",
      label: "Resolved on time",
      value: onTime,
      color: theme.palette.success.main,
    },
    { key: "late", bucket: "LATE", label: "Late", value: late, color: theme.palette.warning.main },
    {
      key: "overdue",
      bucket: "OVERDUE",
      label: "Overdue",
      value: overdue,
      color: theme.palette.error.main,
    },
    {
      key: "inWindow",
      bucket: "PENDING",
      label: "Pending",
      value: inWindow,
      color: theme.palette.grey[400],
    },
  ];
  const segments = allSegments.filter((seg) => seg.value > 0);

  if (segments.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          display: "flex",
          gap: 0.25,
          height: 14,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {segments.map((seg) => (
          <Tooltip key={seg.key} title={`${seg.label}: ${seg.value} — click to filter`} arrow>
            <Box
              role="button"
              tabIndex={0}
              aria-label={`Filter manifests to ${seg.label}: ${seg.value}`}
              onClick={() => onBucketClick(seg.bucket)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onBucketClick(seg.bucket);
                }
              }}
              sx={{
                flexGrow: seg.value,
                flexBasis: 0,
                minWidth: 28,
                bgcolor: seg.color,
                cursor: "pointer",
                outline: "none",
                "&:focus-visible": {
                  boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
                },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {segments.map((seg) => (
          <Stack key={seg.key} direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: seg.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {seg.label} ({seg.value})
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
